import Swal from "sweetalert2";

// Giveaways helpers. Every date typed or shown on these screens is Toronto
// time ("ET"). There is no moment-timezone in this app, so the conversion is
// done with the browser's own Intl time zone data.

export const TZ = "America/Toronto";
export const PURPLE = "#8C50FD";
export const PINK = "#FA69B9";
export const NAVY = "#0A0735";

const partsFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const torontoParts = (date) => {
  const out = {};
  partsFmt.formatToParts(date).forEach((p) => {
    if (p.type !== "literal") out[p.type] = p.value;
  });
  if (out.hour === "24") out.hour = "00";
  return out;
};

// Milliseconds Toronto is ahead of UTC at this instant (negative: behind).
const torontoOffset = (date) => {
  const p = torontoParts(date);
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
};

// "2026-11-01T18:00" typed as Toronto time -> "2026-11-01T23:00:00.000Z".
export const etInputToIso = (value) => {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  let t = guess - torontoOffset(new Date(guess));
  t = guess - torontoOffset(new Date(t));
  return new Date(t).toISOString();
};

// ISO string -> the "YYYY-MM-DDTHH:mm" a datetime-local box shows, in Toronto time.
export const isoToEtInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = torontoParts(d);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

export const fmtEt = (iso, fallback = "Not set") => {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return (
    d.toLocaleString("en-CA", {
      timeZone: TZ,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }) + " ET"
  );
};

export const STATUS_LABELS = {
  draft: "Draft",
  scheduled: "Scheduled",
  active: "Active",
  closed: "Closed",
  drawn: "Winner drawn",
  completed: "Completed",
  archived: "Archived",
  paused: "Paused",
};

export const PHASE_LABELS = {
  hidden: "Page hidden from the public",
  upcoming: "Page shows a coming soon message",
  open: "Open for entries",
  paused: "Paused, entries not accepted",
  closed: "Closed, entries not accepted",
};

// Where each status can go next, and how the button reads.
export const STATUS_MOVES = {
  draft: ["scheduled", "active", "archived"],
  scheduled: ["active", "draft", "closed"],
  active: ["closed"],
  closed: ["archived"],
  completed: ["archived"],
};

export const MOVE_LABELS = {
  scheduled: "Schedule it",
  active: "Open it now",
  draft: "Back to draft",
  closed: "Close entries",
  archived: "Archive",
};

export const ENTRY_STATUS_LABELS = {
  valid: "Valid",
  pending_review: "Needs review",
  duplicate: "Duplicate",
  disqualified: "Disqualified",
};

export const DRAW_STATUS_LABELS = {
  selected: "Selected, not contacted yet",
  contacted: "Contacted",
  skill_passed: "Skill-testing question answered correctly",
  skill_failed: "Answered incorrectly",
  confirmed: "Confirmed eligible winner",
  awarded: "Prize delivered",
  declined: "Declined",
  unresponsive: "Did not respond in time",
  disqualified: "Disqualified",
};

export const DRAW_STEP_BUTTONS = {
  contacted: "Mark contacted",
  skill_passed: "Skill-testing question answered correctly",
  skill_failed: "Answered incorrectly",
  confirmed: "Confirmed eligible winner",
  awarded: "Prize delivered",
  declined: "Declined",
  unresponsive: "Did not respond in time",
  disqualified: "Disqualified",
};

export const DRAW_MOVES = {
  selected: ["contacted", "disqualified", "unresponsive", "declined"],
  contacted: ["skill_passed", "skill_failed", "unresponsive", "declined", "disqualified"],
  skill_passed: ["confirmed", "declined", "disqualified"],
  confirmed: ["awarded", "declined", "disqualified"],
};

// Steps that end this person's chance: a note is required.
export const DEAD_ENDS = ["skill_failed", "declined", "unresponsive", "disqualified"];

// Turn an axios error into { status, message, list } using the server's words.
export const apiProblem = (err, fallback = "Something went wrong. Please try again.") => {
  const status = err?.response?.status;
  const body = err?.response?.data || {};
  let message = body.message || fallback;
  if (status === 403 && !body.message) message = "Only super admins can use Giveaways.";
  if (!err?.response) message = "Could not reach the server. Check your connection and try again.";
  const list = []
    .concat(Array.isArray(body.blockers) ? body.blockers : [])
    .concat(Array.isArray(body.reasons) ? body.reasons : []);
  return { status, message, list };
};

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Success toast, purple tick (never green).
export const toastOk = (title) =>
  Swal.fire({
    icon: "success",
    iconColor: PURPLE,
    title,
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 2500,
  });

// A failed action: the server's message plus any list of reasons, in a dialog
// so a list of blockers can actually be read.
export const showProblem = (problem, title = "That did not go through") => {
  const items = (problem.list || []).map((x) => `<li style="margin-bottom:4px">${esc(x)}</li>`).join("");
  return Swal.fire({
    icon: "warning",
    iconColor: PINK,
    title,
    html:
      `<p style="margin:0 0 8px">${esc(problem.message)}</p>` +
      (items ? `<ul style="text-align:left;margin:0;padding-left:20px">${items}</ul>` : ""),
    confirmButtonText: "OK",
    confirmButtonColor: PURPLE,
  });
};

export const isSuperAdmin = () => localStorage.getItem("user_role") === "super_admin";
